import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { tr } from "@/lib/tr";
import MarketScanner from "./MarketScanner";
import HowToPlay from "./HowToPlay";
import { useAutoRun, AutoRunBar } from "./AutoRun";
import "./arcade-anim.css";

/* 💎 Digit Mines — REAL Mines feel on real Deriv digit contracts.
   10 covered tiles, no numbers. Choose how many 💣 bombs hide on the board:
   1 (~+6% per gem) · 2 (~+19%) · 3 (~+36%). Tap a covered tile — a real
   contract fires and the next tick decides: your tile flips 💎 gem (win) or
   💥 bomb (stake/pot gone). Bomb chance is EXACTLY bombs-in-10 on every tap.
   Gems stay revealed while your pot rides; CASH OUT banks it anytime. */
const MODES = {
  1: { contract: () => ({ contractType: "DIGITDIFF", barrier: Math.floor(Math.random() * 10) }), pay: "~+6%", lose: "1 in 10" },
  2: { contract: () => ({ contractType: "DIGITUNDER", barrier: 8 }), pay: "~+19%", lose: "2 in 10" },
  3: { contract: () => ({ contractType: "DIGITUNDER", barrier: 7 }), pay: "~+36%", lose: "3 in 10" },
};

const LuckyNumber = ({ runContract, busy }) => {
  const [bombs, setBombs] = useState(1);
  const [revealing, setRevealing] = useState(false);
  const [pending, setPending] = useState(null);   // tile being revealed
  const [gems, setGems] = useState([]);           // revealed safe tiles (ride)
  const [boomTile, setBoomTile] = useState(null);
  const [bombPeek, setBombPeek] = useState([]);   // other bombs shown on a boom
  const [res, setRes] = useState(null);
  const [pot, setPot] = useState(null);
  const [rideWins, setRideWins] = useState(0);
  const [tally, setTally] = useState({ hit: 0, miss: 0, net: 0 });
  const [mkt, setMkt] = useState(null);
  const symRef = useRef("1HZ100V");
  const potRef = useRef(null);
  const gemsRef = useRef([]);
  const timers = useRef([]);
  const onScan = useCallback((row) => { symRef.current = row.sym; setMkt(row); }, []);
  useEffect(() => {
    const t = timers.current;
    return () => t.forEach(clearTimeout);
  }, []);

  const clearBoard = () => {
    gemsRef.current = [];
    setGems([]); setBoomTile(null); setBombPeek([]); setRes(null);
  };

  const reveal = async (tile) => {
    if (revealing || busy) return null;
    if (boomTile != null) clearBoard();
    if (gemsRef.current.includes(tile)) return null;
    setPending(tile); setRevealing(true); setRes(null);
    const { contractType, barrier } = MODES[bombs].contract();
    const ridePot = potRef.current;
    const r = await runContract({
      contractType, barrier,
      side: bombs === 1 ? `MINES 1💣` : `MINES ${bombs}💣`,
      label: "Digit Mines", symbol: symRef.current,
      customStake: ridePot ?? undefined,
    });
    setRevealing(false); setPending(null);
    if (!r) return null;
    setRes(r);
    setTally((t) => ({ hit: t.hit + (r.won ? 1 : 0), miss: t.miss + (r.won ? 0 : 1), net: Math.round((t.net + r.profit) * 100) / 100 }));
    if (r.won) {
      gemsRef.current = [...gemsRef.current, tile];
      setGems(gemsRef.current);
      const newPot = Math.round(((ridePot ?? r.stake) + r.profit) * 100) / 100;
      potRef.current = newPot;
      setPot(newPot);
      setRideWins((w) => w + 1);
      // 🏆 board cleared — every safe tile found, bank it automatically
      if (gemsRef.current.length >= 10 - bombs) {
        toast.success(tr("mines_cleared", { n: 10 - bombs, amt: newPot.toFixed(2) }), { duration: 6000 });
        potRef.current = null;
        setPot(null); setRideWins(0);
        timers.current.push(setTimeout(clearBoard, 2200));
      }
    } else {
      setBoomTile(tile);
      // 💣 cosmetic: show where the "other bombs" sat among covered tiles
      const covered = Array.from({ length: 10 }, (_, i) => i).filter((i) => i !== tile && !gemsRef.current.includes(i));
      setBombPeek(covered.sort(() => Math.random() - 0.5).slice(0, bombs - 1));
      potRef.current = null;
      setPot(null); setRideWins(0);
      timers.current.push(setTimeout(clearBoard, 2600));
    }
    return r;
  };
  const randomCovered = () => {
    const covered = Array.from({ length: 10 }, (_, i) => i).filter((i) => !gemsRef.current.includes(i));
    return covered[Math.floor(Math.random() * covered.length)] ?? 0;
  };
  const cashOut = () => {
    const banked = pot;
    potRef.current = null;
    setPot(null); setRideWins(0);
    clearBoard();
    if (banked) {
      toast.success(tr("mines_cashout_title", { amt: banked.toFixed(2), n: rideWins }), { description: tr("mines_cashout_desc") });
    }
  };
  const auto = useAutoRun("lucky");

  return (
    <div data-testid="lucky-game" className="space-y-4">
      <HowToPlay id="lucky"
        steps={[
          "Choose how many 💣 bombs hide on the board — more bombs = bigger payout per gem (1 bomb ~+6% · 2 bombs ~+19% · 3 bombs ~+36%) but a bigger chance of hitting one.",
          "Tap any covered tile. A real Deriv digit contract fires and the next market tick decides your tile's fate: 💎 gem = win, 💥 bomb = stake gone. Bomb chance is EXACTLY bombs-in-10 on every tap.",
          "Gems stay revealed and your pot RIDES — the whole pot goes on the next tile and grows every gem. 💰 CASH OUT banks it anytime; one bomb takes the entire pot.",
          "Clear every safe tile and the board pays out automatically with a 🏆. Each tap is a fresh bombs-in-10 — tiles have no memory.",
        ]}
        win="the next tick's digit lands safe for your contract — your tapped tile turns 💎."
        payout="per gem: ~+6% (1💣) · ~+19% (2💣) · ~+36% (3💣) of the stake/pot — a 💥 takes the whole stake or pot."
        tip="the pot grows fast with 3 bombs but one 💥 erases it — cash out early and often; stopping while ahead beats every streak." />

      <MarketScanner id="lucky" mode="match" onChange={onScan}
        note="the scanner only picks WHICH market your contracts run on · tiles have no digits — every tap stays exactly bombs-in-10" />

      {/* 💣 bomb count picker */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[9px] font-mono uppercase tracking-[0.2em] text-white/40">Bombs</span>
        {[1, 2, 3].map((n) => (
          <button key={n} data-testid={`mines-bombs-${n}`} disabled={revealing || !!pot || auto.active}
            onClick={() => { setBombs(n); clearBoard(); }}
            className={`px-3 h-9 rounded-full border text-[10px] font-mono font-black transition-colors disabled:opacity-40 ${bombs === n ? "border-[#F23645] bg-[#F23645]/15 text-[#F23645]" : "border-white/15 text-white/45 hover:border-white/35"}`}>
            {"💣".repeat(n)} {MODES[n].pay}
          </button>
        ))}
        <span className="text-[8px] font-mono text-white/35">hit chance {MODES[bombs].lose} per tap · bigger payout = more bombs</span>
      </div>

      {/* 🧨 the board — covered tiles, like real Mines */}
      <div data-testid="mines-board" className="grid grid-cols-5 gap-2">
        {Array.from({ length: 10 }, (_, i) => {
          const isGem = gems.includes(i);
          const isBoom = boomTile === i;
          const isPeek = bombPeek.includes(i);
          const isPending = pending === i;
          return (
            <button key={i} data-testid={`mines-tile-${i}`}
              disabled={revealing || auto.active || isGem || boomTile != null}
              onClick={() => reveal(i)}
              className={`h-16 rounded-lg border font-black transition active:scale-95
                ${isGem ? "mine-gem border-[#089981] bg-[#089981]/20" : ""}
                ${isBoom ? "mine-boom border-[#F23645] bg-[#F23645]/25" : ""}
                ${isPeek ? "border-[#F23645]/40 bg-[#F23645]/10" : ""}
                ${!isGem && !isBoom && !isPeek ? "border-white/15 bg-[#1b2130] hover:border-[#F7B731]/60 hover:bg-[#1f2736]" : ""}
                ${isPending ? "animate-pulse border-[#F7B731] bg-[#F7B731]/10" : ""}`}
              style={{ boxShadow: isGem ? "0 0 14px rgba(8,153,129,.35)" : isBoom ? "0 0 18px rgba(242,54,69,.5)" : undefined }}>
              <span className="text-xl leading-none">
                {isGem ? "💎" : isBoom ? "💥" : isPeek ? "💣" : isPending ? "⏳" : "▪️"}
              </span>
            </button>
          );
        })}
      </div>

      {/* result line */}
      {res && !revealing && (
        <div className="arc-pop text-center">
          <p className={`text-lg font-black font-mono ${res.won ? "text-[#089981]" : "text-[#F23645]"}`}>
            {res.won ? "💎 GEM!" : "💥 BOOM!"}
          </p>
          <p className={`text-xs font-mono ${res.won ? "text-[#089981]" : "text-[#F23645]"}`}>
            {res.won ? `+$${res.profit.toFixed(2)}` : `−$${Math.abs(res.profit).toFixed(2)}`}
            {res.digit != null ? ` · tick landed ${res.digit}` : ""}
          </p>
        </div>
      )}

      {/* 💰 pot / cash out */}
      <div className="flex items-stretch gap-2">
        <button data-testid="mines-reveal-btn" disabled={revealing || busy || auto.active || boomTile != null}
          onClick={() => reveal(randomCovered())}
          className="flex-1 h-14 rounded-lg bg-[#089981] hover:bg-[#067a67] text-white font-black text-sm uppercase tracking-wide transition disabled:opacity-40 active:scale-[0.99]">
          {pot ? `⛏ RIDE $${pot.toFixed(2)} — random covered tile` : "⛏ REVEAL a random tile — or tap one yourself"}
        </button>
        {pot && (
          <button data-testid="mines-cashout-btn" disabled={revealing}
            onClick={cashOut}
            className="px-4 h-14 rounded-lg bg-[#F7B731] hover:brightness-110 text-[#0B0E14] font-black text-xs uppercase tracking-wide transition active:scale-95 arc-pop">
            💰 CASH OUT<br />${pot.toFixed(2)}
          </button>
        )}
      </div>
      {pot && (
        <p data-testid="mines-pot-note" className="text-center text-[9px] font-mono text-[#F7B731]">
          🔥 {rideWins} gem{rideWins === 1 ? "" : "s"} riding · one 💥 takes the whole ${pot.toFixed(2)} — cash out keeps it
        </p>
      )}

      <AutoRunBar id="lucky" auto={auto} noun="reveals" />
      {!pot && auto.runs > 1 && (
        <button data-testid="mines-auto-start" disabled={busy || revealing || auto.active}
          onClick={() => auto.burst(() => reveal(randomCovered()))}
          className="w-full h-11 rounded-lg border border-[#2962FF]/50 bg-[#2962FF]/10 text-[#7aa2ff] font-black text-xs uppercase tracking-wide transition active:scale-[0.99] disabled:opacity-40">
          ⚡ Auto-reveal ×{auto.runs} — random tiles back-to-back
        </button>
      )}
      {pot && auto.runs > 1 && <p className="text-[8px] font-mono text-white/40 text-center">auto-play pauses while a pot is riding — cash out or lose it first</p>}

      <div className="text-center space-y-1">
        <p data-testid="lucky-tally" className="text-[11px] font-mono text-white/60">
          Gems <b className="text-[#089981]">{tally.hit}</b> · Booms <b className="text-[#F23645]">{tally.miss}</b> · Net <b className={tally.net >= 0 ? "text-[#089981]" : "text-[#F23645]"}>${tally.net.toFixed(2)}</b>
        </p>
        <p className="text-[10px] font-mono text-white/40">every tap = a real Deriv digit contract on the locked market · the next tick decides, tiles have no memory — each tap is a fresh {bombs}-in-10 · a 💥 takes the whole stake/pot · losses possible</p>
      </div>
    </div>
  );
};

export default LuckyNumber;
